import { useCallback, useLayoutEffect, useRef } from 'react';
import { di } from 'react-magnetic-di';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import { Ati } from '@atlassian/ari/ati';
import { useIntl } from '@atlassian/jira-intl';
import type { MenuItem, NamedItems } from '@atlassian/universal-create/types';
import { useIsInitialLoadLegacyIssueCreate } from '@atlassian/jira-create-issue-legacy/src/controllers/is-initial-load-create-issue-legacy/index.tsx';
import OnCreateClickDI from '@atlassian/jira-create-issue-legacy/src/index.tsx';
import { GLOBAL_CREATE_BUTTON } from '@atlassian/jira-create-issue-legacy/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useInitializePersistentIssueCreateModalView } from '@atlassian/jira-issue-create/src/controllers/use-initialize-persistent-issue-create-modal-view/index.tsx';
import { isMobileAndInMvpOrExistingUsersExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import {
	GENERIC_GLOBAL_CREATE_BUTTON,
	MOBILE_GLOBAL_CREATE_BUTTON,
} from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants.tsx';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import type { TriggerPointKeyType } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/types.tsx';
import {
	type GicOptInStatusResourceData,
	gicOptInStatusResources,
} from '@atlassian/jira-router-resources-global-issue-create-switch/src/index.tsx';

import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import messages from '../messages.tsx';
import { UniversalCreateExperience } from '../experiences.tsx';
import { useUniversalCreateAnalytics } from './use-analytics-tracker.tsx';

// TODO: extract onclick logi https://hello.jira.atlassian.cloud/browse/NAVX-2658
// copied from src/packages/navigation-apps/atlassian-navigation/src/ui/create/utils.tsx to avoid circular dependency
export const shouldDisableGICFlagCreation: () => boolean = () => {
	di(window);
	const JSW_TMP_BACKLOG_BOARD_REGEXP =
		/jira\/software\/projects\/[a-zA-Z0-9]+\/boards\/\d+(\/backlog)?/;
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	return JSW_TMP_BACKLOG_BOARD_REGEXP.test(window.location.pathname);
};

export function useCreateIssueItem(
	onCreateClick = OnCreateClickDI,
	experienceTracker = UniversalCreateExperience,
): MenuItem {
	const { formatMessage } = useIntl();

	const triggerAnalytics = useUniversalCreateAnalytics();

	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	useInitializePersistentIssueCreateModalView();

	const [{ isInitialLoad }, actions] = useIsInitialLoadLegacyIssueCreate();
	let currentIssueCreateVisibility: GicOptInStatusResourceData['gicOptInStatus'] | null = null;
	const gicOptInStateOverridden = useRef(false);
	const isAnonymous = useIsAnonymous();

	const isMobileExperienceEnabled = isMobileAndInMvpOrExistingUsersExperiment();

	// copied from src/packages/navigation-apps/atlassian-navigation/src/ui/create/main.tsx#useLayoutEffect hook
	// TODO: delete this code for clean up issue-create-stop-using-gicoptinstatusresources
	if (fg('render_modern_global_issue_create_modal_experience')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { data, update } = useResource(gicOptInStatusResources);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		useLayoutEffect(() => {
			if (__SERVER__) {
				return;
			}

			if (isAnonymous && !gicOptInStateOverridden.current) {
				update(() => ({
					gicOptInStatus: {
						isEnabled: true,
					},
				}));
				gicOptInStateOverridden.current = true;
			}
		}, [isAnonymous, update]);

		currentIssueCreateVisibility = data && data?.gicOptInStatus;
	}

	const onClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		(e) => {
			if (currentIssueCreateVisibility?.isEnabled) {
				experienceTracker.abort({ force: true });
				experienceTracker.start();
				experienceTracker.addMetadata({
					inputDelay: Math.round(performance.now() - e.timeStamp),
				});

				const nextState: {
					disableCreationSuccessFlag: boolean;
					triggerPointKey: TriggerPointKeyType;
					displayMinimizableButtons?: boolean;
					displayCreateAnotherIssueToggle?: boolean;
				} = {
					disableCreationSuccessFlag: shouldDisableGICFlagCreation(),
					triggerPointKey: GENERIC_GLOBAL_CREATE_BUTTON,
					...(isMobileExperienceEnabled
						? {
								displayMinimizableButtons: false,
								displayCreateAnotherIssueToggle: false,
								triggerPointKey: MOBILE_GLOBAL_CREATE_BUTTON,
							}
						: {}),
				};
				openIssueCreateModal(nextState);
			} else {
				onCreateClick({ isInitialLoad, triggerSource: GLOBAL_CREATE_BUTTON });
				if (isInitialLoad) {
					actions.clearInitialLoaded();
				}
			}
			triggerAnalytics();
		},
		[
			actions,
			currentIssueCreateVisibility,
			experienceTracker,
			isInitialLoad,
			isMobileExperienceEnabled,
			onCreateClick,
			openIssueCreateModal,
			triggerAnalytics,
		],
	);

	return {
		ati: Ati.parse('ati:cloud:jira:issue:create'),
		elemBefore: WorkItemIcon,
		children: formatMessage(messages.createIssue),
		onClick: __SERVER__ ? undefined : onClick,
	};
}

export function useUniversalCreateItems(): NamedItems {
	return {
		items: [],
	};
}
